// ListingDetailsPage.tsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Heart,
  Share2,
  MapPin,
  Star,
  Bed,
  Bath,
  Maximize,
  Wifi,
  Wind,
  Tv,
  Car,
  Coffee,
  Home,
  Building2,
  Clock,
  Calendar,
  User,
  Phone,
  Mail,
  MessageCircle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Navigation,
  ExternalLink,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Users,
  Dumbbell,
  Waves,
  Dog,
  Shield,
  Zap,
  Droplet,
  Eye,
  ArrowUp,
  Sofa,
  BookOpen,
  Umbrella,
  Leaf,
  Loader2,
  AlertTriangle,
  DoorOpenIcon,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MY_ENDPOINTS } from "@vehidhi/shared";
import { useLang } from "@/context/TranslationProvider";
import { useToast } from "@/context/ToastContext";
import { useFetchQuery } from "@/hooks/useQuery";

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Types matching your database schema
interface ApiListing {
  product_id: string;
  tenant_id: string;
  sku: string;
  title: string;
  slug: string;
  description: string | null;
  brand: string | null;
  price: number;
  price_unit: "month" | "night" | "hour" | "day" | "total";
  sale_price: number | null;
  cost_price: number | null;
  stock: number;
  reserved: number;
  low_stock_threshold: number;
  reorder_point: number;
  type: "physical" | "digital" | "service" | "rental";
  attributes: string; // JSON string of amenities
  image_url: string | null;
  gallery: string | null;
  booking_rules: string | null;
  weight: number | null;
  dimensions: string | null;
  property_category: "residential" | "commercial";
  property_type: "apartment" | "room" | "office" | "shop" | "villa" | "commercial" | "house" | "land";
  property_status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  bedrooms: number | null;
  bathrooms: number | null;
  area_sqft: number | null;
  area_sqm: number | null;
  floor_number: number | null;
  total_floors: number | null;
  year_built: number | null;
  furnished: "unfurnished" | "semi-furnished" | "fully-furnished" | null;
  parking_spots: number;
  pet_friendly: number;
  immediate_available: number;
  minimum_lease_months: number | null;
  maximum_lease_months: number | null;
  deposit_amount: number | null;
  maintenance_fee: number | null;
  utilities_included: number;
  viewing_count: number;
  favorite_count: number;
  latitude: number;
  longitude: number;
  address_line1: string | null;
  address_line2: string | null;
  city: string;
  island: string;
  postal_code: string | null;
  virtual_tour_url: string | null;
  status: string;
  is_new: number;
  is_featured: number;
  created_at: string;
  updated_at: string;
}

// API Response type
interface ApiResponse {
  success: boolean;
  data: ApiListing;
  message?: string;
}

// Transformed listing for UI
interface TransformedListing {
  id: string;
  product_id: string;
  title: string;
  description: string;
  price: number;
  priceUnit: "month" | "night" | "hour" | "day" | "total";
  location: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  island: string;
  type: "residential" | "commercial" | "short-stay";
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  amenities: string[];
  status: "available" | "rented" | "maintenance" | "coming_soon" | "sold";
  latitude: number;
  longitude: number;
  images: string[];
  rating?: number;
  reviews?: number;
  isNew: boolean;
  isFeatured: boolean;
  viewingCount: number;
  favoriteCount: number;
  furnished?: "unfurnished" | "semi-furnished" | "fully-furnished";
  parkingSpots: number;
  petFriendly: boolean;
  immediateAvailable: boolean;
  utilitiesIncluded: boolean;
  minimumLeaseMonths?: number;
  maximumLeaseMonths?: number;
  depositAmount?: number;
  maintenanceFee?: number;
  contact: {
    name: string;
    phone: string;
    email: string;
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Helper function to transform API data to UI format
const transformListing = (apiListing: ApiListing): TransformedListing => {
  // Parse amenities from JSON string
  let amenities: string[] = [];
  try {
    amenities = JSON.parse(apiListing.attributes || "[]");
  } catch (e) {
    console.error("Failed to parse amenities:", e);
    amenities = [];
  }

  // Parse gallery images
  let images: string[] = [];
  try {
    if (apiListing.image_url) {
      images.push(apiListing.image_url);
    }
    if (apiListing.gallery) {
      const gallery = JSON.parse(apiListing.gallery);
      if (Array.isArray(gallery)) {
        images = [...images, ...gallery];
      }
    }
  } catch (e) {
    console.error("Failed to parse gallery:", e);
  }

  // Determine UI type
  let type: "residential" | "commercial" | "short-stay" = "residential";
  if (apiListing.property_category === "commercial") {
    type = "commercial";
  } else if (apiListing.price_unit === "night" || apiListing.price_unit === "hour" || apiListing.price_unit === "day") {
    type = "short-stay";
  }

  // Build location string
  const locationParts = [
    apiListing.address_line1,
    apiListing.address_line2,
    apiListing.island,
    apiListing.city,
  ].filter(Boolean);
  const location = locationParts.join(", ");

  // Mock contact info - In production, this would come from the API
  const contact = {
    name: "Property Manager",
    phone: "+960 123-4567",
    email: "info@example.com",
  };

  return {
    id: apiListing.product_id,
    product_id: apiListing.product_id,
    title: apiListing.title,
    description: apiListing.description || "No description provided.",
    price: apiListing.price,
    priceUnit: apiListing.price_unit,
    location: location || "Location not specified",
    address_line1: apiListing.address_line1 || "",
    address_line2: apiListing.address_line2 || undefined,
    city: apiListing.city,
    island: apiListing.island,
    type,
    bedrooms: apiListing.bedrooms || undefined,
    bathrooms: apiListing.bathrooms || undefined,
    area: apiListing.area_sqft || apiListing.area_sqm || undefined,
    amenities,
    status: apiListing.property_status,
    latitude: apiListing.latitude,
    longitude: apiListing.longitude,
    images: images.length ? images : [],
    isNew: apiListing.is_new === 1,
    isFeatured: apiListing.is_featured === 1,
    viewingCount: apiListing.viewing_count,
    favoriteCount: apiListing.favorite_count,
    furnished: apiListing.furnished || undefined,
    parkingSpots: apiListing.parking_spots,
    petFriendly: apiListing.pet_friendly === 1,
    immediateAvailable: apiListing.immediate_available === 1,
    utilitiesIncluded: apiListing.utilities_included === 1,
    minimumLeaseMonths: apiListing.minimum_lease_months || undefined,
    maximumLeaseMonths: apiListing.maximum_lease_months || undefined,
    depositAmount: apiListing.deposit_amount || undefined,
    maintenanceFee: apiListing.maintenance_fee || undefined,
    contact,
    createdAt: apiListing.created_at,
    updatedAt: apiListing.updated_at,
  };
};

// Amenity icons mapping
const amenityIcons: Record<string, any> = {
  "Air Conditioning": Wind, AC: Wind,
  "WiFi": Wifi, Wifi: Wifi,
  "TV": Tv,
  "Parking": Car,
  "Kitchen": Home,
  "Washing Machine": Home,
  "Dryer": Home,
  "Gym": Dumbbell,
  "Pool": Waves,
  "Hot Water": Droplet,
  "Elevator": ArrowUp,
  "Security": Shield,
  "Balcony": Home,
  "Garden": Leaf,
  "Beach Access": Umbrella,
  "Furnished": Sofa,
  "Pets Allowed": Dog,
  "Smoking Allowed": Coffee,
  "Wheelchair Access": Users,
  "Study Desk": BookOpen,
  "Ocean View": Eye,
  "City View": Eye,
  "Private Entrance": DoorOpenIcon,
  "Backup Generator": Zap,
  "Water Tank": Droplet,
};

// Image Gallery Component
const ImageGallery = ({ images, title }: { images: string[]; title: string }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!images.length) {
    return (
      <div className="h-96 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl flex items-center justify-center">
        <Home className="w-20 h-20 text-gray-400" />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <>
      <div className="relative h-96 rounded-2xl overflow-hidden group">
        <img
          src={images[currentIndex]}
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover cursor-pointer"
          onClick={() => setIsFullscreen(true)}
        />
        
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
        
        <div className="absolute bottom-4 right-4 px-2 py-1 bg-black/50 rounded-lg text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
            <img
              src={images[currentIndex]}
              alt={title}
              className="max-w-[90vw] max-h-[90vh] object-contain"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Map Component
const ListingMap = ({ lat, lng, title }: { lat: number; lng: number; title: string }) => {
  const position: [number, number] = [lat, lng];
  
  if (!lat || !lng) {
    return (
      <div className="h-64 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <p className="text-gray-500">Location not available</p>
      </div>
    );
  }

  return (
    <div className="h-64 rounded-xl overflow-hidden">
      <MapContainer
        center={position}
        zoom={15}
        className="h-full w-full"
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={position}>
          <Popup>{title}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

// Amenities Grid Component
const AmenitiesGrid = ({ amenities }: { amenities: string[] }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {amenities.map((amenity) => {
        const Icon = amenityIcons[amenity] || CheckCircle;
        return (
          <div key={amenity} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Icon className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">{amenity}</span>
          </div>
        );
      })}
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ contact, onContact }: { contact: TransformedListing['contact']; onContact?: () => void }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(contact.phone);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <User className="w-5 h-5 text-purple-500" />
        Contact Agent
      </h3>
      
      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
          {contact.name.charAt(0)}
        </div>
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">{contact.name}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">Property Agent</p>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleCopyPhone}
          className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-purple-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">{contact.phone}</span>
          </div>
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400" />
          )}
        </button>
        
        <a
          href={`mailto:${contact.email}`}
          className="w-full flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow"
        >
          <Mail className="w-4 h-4 text-purple-500" />
          <span className="text-sm text-gray-700 dark:text-gray-300">{contact.email}</span>
        </a>
        
        <button
          onClick={onContact}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Send Message
        </button>
      </div>
    </div>
  );
};

// Loading Component
const LoadingState = () => (
  <div className="fixed inset-0 bg-white dark:bg-gray-900 flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Loading property details...</p>
    </div>
  </div>
);

// Error Component
const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto px-4">
      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Property</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
      <button
        onClick={onRetry}
        className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
      >
        Try Again
      </button>
    </div>
  </div>
);

// Not Found Component
const NotFoundState = () => (
  <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
    <div className="text-center max-w-md mx-auto px-4">
      <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Property Not Found</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        The property you're looking for doesn't exist or has been removed.
      </p>
      <button
        onClick={() => window.history.back()}
        className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

// Main Component
const ListingDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLang();
  const toast = useToast();
  const [isLiked, setIsLiked] = useState(false);
  const [listing, setListing] = useState<TransformedListing | null>(null);

  // Fetch listing details
  const { data, isLoading, error, refetch } = useFetchQuery<ApiResponse>(
    ['listing', id],
    MY_ENDPOINTS.PRODUCT_ENDPOINTS.GET_PRODUCT.path.replace(':id', id || ''),
    {
      enabled: !!id,
    }
  );

  // Transform data when received
  useEffect(() => {
    if (data?.success && data?.data) {
      const transformed = transformListing(data.data);
      setListing(transformed);
      
      // Update document title
      document.title = `${transformed.title} - Rentify Maldives`;
    }
  }, [data]);

  const handleLike = async () => {
    setIsLiked(!isLiked);
    // Call API to toggle favorite
    try {
      // await api.post(`/products/${id}/favorite`)
      toast.show({
        title: isLiked ? "Removed from favorites" : "Added to favorites",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleContact = () => {
    toast.show({
      title: "Contact Agent",
      description: "This feature will be available soon.",
      type: "info",
    });
  };

  const handleClose = () => {
    navigate(-1);
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return (
      <ErrorState 
        message={error.message || "Failed to load property details"} 
        onRetry={refetch}
      />
    );
  }

  // Not found state
  if (!listing) {
    return <NotFoundState />;
  }


    return (
    <div className="">Hi</div>
  )


  // Main component render
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-white dark:bg-gray-900"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleClose}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <div className="flex items-center gap-2">
              <button
                onClick={handleLike}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Heart
                  className={`w-5 h-5 transition-colors ${
                    isLiked ? "fill-red-500 text-red-500" : "text-gray-600 dark:text-gray-400"
                  }`}
                />
              </button>
              <button
                onClick={() => {
                  navigator.share?.({
                    title: listing.title,
                    text: `Check out this ${listing.type} property`,
                    url: window.location.href,
                  }).catch(() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.show({
                      title: "Link copied to clipboard",
                      type: "success",
                    });
                  });
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <ImageGallery images={listing.images} title={listing.title} />
            
            {/* Title and Basic Info */}
            <div>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {listing.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 text-gray-500 dark:text-gray-400">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm">{listing.location}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {listing.priceUnit === "month" && "MVR "}
                    {listing.price.toLocaleString()}
                    {listing.priceUnit === "month" && "/month"}
                    {listing.priceUnit === "night" && "/night"}
                    {listing.priceUnit === "hour" && "/hour"}
                    {listing.priceUnit === "day" && "/day"}
                  </div>
                  {listing.status === "available" && (
                    <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      Available
                    </div>
                  )}
                </div>
              </div>
              
              {/* Stats Row */}
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                {listing.bedrooms !== undefined && (
                  <div className="flex items-center gap-2">
                    <Bed className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Bedrooms</div>
                      <div className="font-semibold">{listing.bedrooms}</div>
                    </div>
                  </div>
                )}
                {listing.bathrooms !== undefined && (
                  <div className="flex items-center gap-2">
                    <Bath className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Bathrooms</div>
                      <div className="font-semibold">{listing.bathrooms}</div>
                    </div>
                  </div>
                )}
                {listing.area !== undefined && (
                  <div className="flex items-center gap-2">
                    <Maximize className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Area</div>
                      <div className="font-semibold">{listing.area} sqft</div>
                    </div>
                  </div>
                )}
                {listing.parkingSpots > 0 && (
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Parking</div>
                      <div className="font-semibold">{listing.parkingSpots} spot{listing.parkingSpots !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Description</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                {listing.description}
              </p>
            </div>
            
            {/* Amenities */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Amenities</h2>
              <AmenitiesGrid amenities={listing.amenities} />
            </div>
            
            {/* Location Map */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Location</h2>
              <ListingMap lat={listing.latitude} lng={listing.longitude} title={listing.title} />
              {listing.latitude && listing.longitude && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-purple-500 hover:text-purple-600 text-sm font-medium"
                >
                  <Navigation className="w-4 h-4" />
                  Get Directions
                </a>
              )}
            </div>
          </div>
          
          {/* Right Column - Contact and Additional Info */}
          <div className="space-y-6">
            {/* Contact Card */}
            <ContactCard contact={listing.contact} onContact={handleContact} />
            
            {/* Additional Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Additional Information</h3>
              <div className="space-y-3">
                {listing.furnished && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Furnishing</span>
                    <span className="text-sm font-medium capitalize">{listing.furnished.replace('-', ' ')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Property Type</span>
                  <span className="text-sm font-medium capitalize">{listing.type}</span>
                </div>
                {listing.petFriendly && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Pets</span>
                    <span className="text-sm font-medium text-green-600">Allowed</span>
                  </div>
                )}
                {listing.utilitiesIncluded && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Utilities</span>
                    <span className="text-sm font-medium text-green-600">Included</span>
                  </div>
                )}
                {listing.immediateAvailable && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Availability</span>
                    <span className="text-sm font-medium text-green-600">Immediate</span>
                  </div>
                )}
                {listing.minimumLeaseMonths && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Min. Lease</span>
                    <span className="text-sm font-medium">{listing.minimumLeaseMonths} months</span>
                  </div>
                )}
                {listing.maximumLeaseMonths && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Max. Lease</span>
                    <span className="text-sm font-medium">{listing.maximumLeaseMonths} months</span>
                  </div>
                )}
                {listing.depositAmount && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Deposit</span>
                    <span className="text-sm font-medium">MVR {listing.depositAmount.toLocaleString()}</span>
                  </div>
                )}
                {listing.maintenanceFee && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Maintenance Fee</span>
                    <span className="text-sm font-medium">MVR {listing.maintenanceFee.toLocaleString()}/month</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Views</span>
                  <span className="text-sm font-medium">{listing.viewingCount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Saved</span>
                  <span className="text-sm font-medium">{listing.favoriteCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
            
            {/* Listing ID */}
            <div className="text-center text-xs text-gray-400 dark:text-gray-600">
              Listing ID: {listing.product_id}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ListingDetailsPage;